import { baseConstraintKey } from '@bp/shared-planung-utils';
import type { BreakdownRow } from './solverDiagnostics';

/**
 * „Noch offen“-Zeile im Solver-Banner: die größten Strafthemen des aktuell besten Plans, mit Trend
 * gegenüber dem vorigen Poll. Reine Funktionen, damit Auswahl, Reihenfolge und Trend testbar sind.
 */

export type OpenTopicTrend = 'down' | 'up' | 'same' | 'new';

export type OpenTopic = {
  /** Basis-Key der Regel (ohne Paket-Präfix und Tier-Suffix), z. B. `subject2009`, `class1003-nogap`. */
  id: string;
  /** Anzahl der Verstöße (`matchCount`). */
  count: number;
  trend: OpenTopicTrend;
};

export type OpenTopicsState = {
  jobId: string;
  /** Kennung des Polls, aus dem `current` stammt; derselbe Poll ändert den Zustand nicht. */
  pollKey: string;
  /** Anzahl je Regel aus dem vorigen Poll (leer beim ersten Poll des Jobs). */
  previous: Record<string, number>;
  current: Record<string, number>;
  topics: OpenTopic[];
};

export const MAX_OPEN_TOPICS = 3;

type Aggregated = { id: string; hard: number; medium: number; soft: number; count: number };

/** Bare key: Paket-Präfix (`pkg/teacher0900`) und Tier-Suffix (`-hard/-medium/-soft`) entfernen. */
export function openTopicKey(name: string): string {
  const bare = name.includes('/') ? (name.split('/').pop() ?? name) : name;
  return baseConstraintKey(bare);
}

/** 0 = hart, 1 = mittel, 2 = weich; null = keine Strafe (Belohnung oder 0). */
function severityTier(row: Aggregated): 0 | 1 | 2 | null {
  if (row.hard < 0) return 0;
  if (row.medium < 0) return 1;
  if (row.soft < 0) return 2;
  return null;
}

function magnitude(row: Aggregated, tier: 0 | 1 | 2): number {
  return Math.abs(tier === 0 ? row.hard : tier === 1 ? row.medium : row.soft);
}

/**
 * Strafthemen eines Breakdowns, je Regel zusammengefasst (Tier-Varianten zählen als eine Regel),
 * nur mit Verstößen (`matchCount > 0`) und negativem Score — Belohnungen (positiver Score) fallen weg.
 * Sortiert hart vor mittel vor weich, darin nach Betrag des Scores.
 */
export function selectOpenTopicCounts(rows: readonly BreakdownRow[], max = MAX_OPEN_TOPICS): Array<[string, number]> {
  const byId = new Map<string, Aggregated>();
  for (const row of rows) {
    if (!row || typeof row.name !== 'string' || !(row.matchCount > 0)) continue;
    const id = openTopicKey(row.name);
    const agg = byId.get(id) ?? { id, hard: 0, medium: 0, soft: 0, count: 0 };
    agg.hard += row.hardScore || 0;
    agg.medium += row.mediumScore || 0;
    agg.soft += row.softScore || 0;
    agg.count += row.matchCount;
    byId.set(id, agg);
  }
  return [...byId.values()]
    .map((row) => ({ row, tier: severityTier(row) }))
    .filter((e): e is { row: Aggregated; tier: 0 | 1 | 2 } => e.tier !== null)
    .sort(
      (a, b) =>
        a.tier - b.tier || magnitude(b.row, b.tier) - magnitude(a.row, a.tier) || a.row.id.localeCompare(b.row.id),
    )
    .slice(0, max)
    .map(({ row }) => [row.id, row.count]);
}

function trendOf(count: number, previous: number | undefined): OpenTopicTrend {
  if (previous === undefined) return 'new';
  if (count < previous) return 'down';
  if (count > previous) return 'up';
  return 'same';
}

/**
 * Nächster Zustand der „Noch offen“-Zeile. Idempotent je `pollKey` (ein Re-Render ohne neuen Poll
 * verschiebt den Trend nicht); ein anderer Job beginnt ohne Vorgänger.
 * `rows === null` heißt: dieser Poll brachte keinen Breakdown — die letzte Anzeige bleibt stehen.
 */
export function advanceOpenTopics(
  state: OpenTopicsState | null,
  input: { jobId: string; pollKey: string; rows: readonly BreakdownRow[] | null },
): OpenTopicsState | null {
  const sameJob = state?.jobId === input.jobId;
  if (input.rows === null) {
    return sameJob ? state : null;
  }
  if (sameJob && state.pollKey === input.pollKey) {
    return state;
  }
  const previous = sameJob ? state.current : {};
  const selected = selectOpenTopicCounts(input.rows);
  // Alle Regeln merken, nicht nur die angezeigten — sonst wäre eine Regel, die wieder unter die ersten
  // drei rutscht, „neu“ statt mit Trend.
  const current: Record<string, number> = {};
  for (const [id, count] of selectOpenTopicCounts(input.rows, Number.POSITIVE_INFINITY)) {
    current[id] = count;
  }
  return {
    jobId: input.jobId,
    pollKey: input.pollKey,
    previous,
    current,
    topics: selected.map(([id, count]) => ({ id, count, trend: trendOf(count, previous[id]) })),
  };
}
